/**
 * WFS access for the prospect layers.
 *
 * These are published from `au_pathfinder.prospect_features` through six
 * filtered views, in the `gold-finder-pathfinder` datastore — a SECOND store,
 * because the existing `gold-finder-imported` is pinned to schema=wa_geodata and
 * structurally cannot see au_pathfinder.
 *
 * THE BBOX FORM IS NOT INTERCHANGEABLE. These featuretypes declare
 * EPSG:404000 (see goldfinder-geoserver/LAYERS.md §2), so a BBOX with no CRS
 * argument is read in the declared CRS's authority axis order — GeoServer takes
 * the client's x,y as lat,lon, matches nothing, and returns an empty
 * FeatureCollection with HTTP 200. Verified live on every one of the six
 * layers: `BBOX(geom,119.9,-30.55,120.15,-30.40)` returns 0 features while the
 * same box with `,'EPSG:4326'` appended returns 5. Naming the CRS explicitly is
 * what makes lng,lat mean lng,lat, so do not drop that argument to tidy the
 * string up. Returned geometry is [lng, lat] either way.
 */
import axios from 'axios';

import { CATEGORIES, drillPpbOf, rowTopDepthM, withinReach } from './prospectStyle';

const geoserverBase = (process.env.REACT_APP_GOLD_FINDER_GEOSERVER || '').replace(/\/$/, '');
const GoldFinderGeoserver = axios.create({ baseURL: geoserverBase });

/** A whole-of-state fetch is still small, but never unbounded. */
const MAX_FEATURES = 20000;
const TIMEOUT_MS = 30000;

export const EMPTY = { type: 'FeatureCollection', features: [] };

/** First latitude in a geometry of any type, or null. */
function latOf(geom) {
  if (!geom) return null;
  let c = geom.coordinates;
  while (Array.isArray(c) && Array.isArray(c[0])) [c] = c;
  return Array.isArray(c) && typeof c[1] === 'number' ? c[1] : null;
}

/** `[w,s,e,n]` from a MapLibre LngLatBounds, as CQL wants it. */
export const boundsToCql = (b) =>
  `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()},'EPSG:4326'`;

/**
 * Which rows belong to the same hole.
 *
 * The same pairing the popup uses to gather a hole's intervals: a hole number is
 * only unique within the report it came out of, so two reports can each have a
 * "WAB204" and they are not the same hole. A row with no hole number is its own
 * group — it is a standalone mark, not part of anything.
 */
const holeKey = (p = {}, i) => (p.hole ? `${p.anumber}/${p.hole}` : `row-${i}`);

/**
 * Mark the one row of each hole that the map draws, and whether the hole carries
 * a value anywhere.
 *
 * Every row of a hole shares one point, so drawing the view as it arrives stacks
 * a hole's collar and all its assayed intervals on that point — 426 of 764 rows
 * sit on a shared point, one of them carries 43. The symbol you actually saw was
 * whichever symbol-sort-key lifted; the rest were drawn underneath it, invisible
 * and hit-testable, and they are the same depths the popup lists in a table.
 *
 * So the RICHEST row of each hole is stamped `hole_top` and is the only one
 * drawn. It is the row that sort key was already putting on top, so the map does
 * not change — it just stops painting the same hole once per depth. Ties, and
 * holes where nothing carries a value, fall to the collar row, which is the one
 * holding the hole's position and metadata.
 *
 * `reach_ppb` — the best value in the first REACH_M metres, or -1 — is stamped
 * on every row of the hole, because no single row can answer that by itself: the
 * collar carries the position and never a grade. It is what the mark is coloured
 * by, what the value filter tests, and what the collar halo follows so it cannot
 * outlive the mark it belongs to.
 */
export function stampHoles(features) {
  const groups = new Map();
  features.forEach((f, i) => {
    const key = holeKey(f.properties, i);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(i);
  });

  const top = new Set();
  // The JS twin of IS_COLLAR: no ordinal and no depth. Tested for EMPTINESS, not
  // for falsiness — `from_m` of 0 is a real depth and `ordinal` of 0 a real
  // position in the hole, and both are falsy.
  const empty = (v) => v === null || v === undefined || v === '';
  const isCollarRow = (p) => empty(p.ordinal) && empty(p.from_m);

  /** Per-hole facts, stamped onto every row of that hole. */
  const summary = new Map();

  groups.forEach((idxs, key) => {
    let best = idxs[0];
    let bestPpb = -Infinity;
    let deepRows = 0;

    idxs.forEach((i) => {
      const p = features[i].properties || {};
      const ppb = drillPpbOf(p);
      const reachable = withinReach(p);
      // Count only rows that HAVE something to hide. A valueless interval below
      // 30 m is nothing to tell the reader they are missing.
      if (!reachable && ppb >= 0) deepRows += 1;
      // Out-of-reach rows cannot win: the mark describes workable ground, and a
      // 48 m intercept deciding the colour is the whole bug this fixes.
      if (!reachable) return;
      // The collar wins a tie, so a hole with nothing assayed within reach is
      // still represented by the row carrying its position and metadata.
      if (ppb > bestPpb || (ppb === bestPpb && isCollarRow(p))) { best = i; bestPpb = ppb; }
    });

    top.add(best);
    const chosen = features[best].properties || {};
    const reachPpb = bestPpb >= 0 ? bestPpb : -1;
    const topDepth = reachPpb >= 0 ? rowTopDepthM(chosen) : null;
    summary.set(key, {
      reach_ppb: reachPpb,
      // Where that value sits, and whether the depth was printed or inferred
      // from tick spacing — the popup must not quote an inferred depth as fact.
      reach_from_m: topDepth,
      reach_to_m: topDepth === null ? null : (empty(chosen.to_m) ? null : Number(chosen.to_m)),
      reach_inferred: topDepth !== null && empty(chosen.from_m),
      // Values below REACH_M, hidden by default. The COUNT is always carried so
      // the popup can say they exist without showing them — a hole that stops at
      // 30 m on screen must not read as a hole that stopped at 30 m in the
      // ground.
      deep_rows: deepRows,
    });
  });

  return features.map((f, i) => {
    const props = f.properties || {};
    return {
      ...f,
      properties: {
        ...props,
        ...summary.get(holeKey(props, i)),
        hole_top: top.has(i),
        // Per ROW, for the popup's downhole table.
        row_deep: !withinReach(props),
      },
    };
  });
}

/**
 * Fetch one category over a bbox.
 *
 * `kind` is stamped onto every feature here rather than read from the data: the
 * views do not select that column (prospect_lines is the exception, since it
 * merges corridors and detector lines and needs to tell them apart). Everything
 * downstream — hit-testing, the popup, the layer filters — asks what a mark is,
 * and this is the one place that can answer without a second round trip.
 */
export async function fetchCategory({ typename, kind }, bboxCql, signal) {
  const { data } = await GoldFinderGeoserver.get('/ne/ows', {
    signal,
    timeout: TIMEOUT_MS,
    params: {
      service: 'WFS',
      version: '1.1.0',
      request: 'GetFeature',
      typeNames: typename,
      outputFormat: 'application/json',
      maxFeatures: MAX_FEATURES,
      cql_filter: `BBOX(geom,${bboxCql})`,
    },
  });
  if (!data || !Array.isArray(data.features)) throw new Error('malformed WFS response');
  // `lat` is stamped here because a paint expression cannot read geometry.
  // The collar halo draws accuracy_m as a real circle on the ground, and
  // converting metres to screen pixels in web mercator needs the feature's own
  // latitude. Deriving it here keeps a denormalised copy of y out of the table.
  const features = data.features.map((f) => {
    const lat = latOf(f.geometry);
    return {
      ...f,
      properties: {
        ...f.properties,
        ...(kind ? { kind } : null),
        ...(lat === null ? null : { lat }),
      },
    };
  });
  return {
    ...data,
    // Only the drill view carries several rows per thing; a soil sample and a
    // finding are one row each.
    features: kind === 'drill_hole' ? stampHoles(features) : features,
  };
}

/**
 * Fetch the given categories for one viewport.
 *
 * Categories are independent requests so a slow or broken one cannot blank the
 * others — a failure returns an empty collection for that category alone and
 * the caller is told which ones failed. Six parallel requests is well inside
 * what the browser will open to one host.
 *
 * `cats` is what the sidebar's sub-filter selects, which is why the request is
 * skipped rather than the result hidden: a category nobody is looking at should
 * not cost a round trip on every pan. This is the shape the one-source-per-view
 * split was for.
 */
export async function fetchAll(bboxCql, signal, cats = CATEGORIES) {
  const results = await Promise.all(cats.map(async (cat) => {
    try {
      return [cat.key, await fetchCategory(cat, bboxCql, signal), null];
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') throw err;
      return [cat.key, EMPTY, cat.key];
    }
  }));
  return {
    data: Object.fromEntries(results.map(([k, fc]) => [k, fc])),
    failed: results.map(([, , f]) => f).filter(Boolean),
  };
}

export default fetchAll;
